<script setup lang="ts">
import { ref } from 'vue'
import { useStore } from 'vuex'

import {MoreHorizontal, Pencil} from 'lucide-vue-next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'

import type { ReportTemplate } from '@/types'
import CreateTemplateDialog from "@/pages/Project/Templates/CreateTemplateDialog.vue";

const store = useStore()

const props = defineProps<{
  project_id: number
  template: ReportTemplate
}>()

const remove = async (ev: any) => {
  store.dispatch('geo/deleteReportTemplate', {
    project_id: props.project_id,
    template: props.template
  })
}
</script>

<template>
  <div class="flex items-center">
    <!-- Only show edit button if not a system template -->
    <CreateTemplateDialog
      v-if="!props.template.is_system_template"
      :project-id="props.project_id"
      :template="props.template"
      :edit-mode="true"
    >
      <Button variant="ghost" size="sm" data-testid="template-edit-button"><Pencil class="w-3.5 h-3.5" /></Button>
    </CreateTemplateDialog>

    <!-- Show info button for system templates -->
    <Button
      v-else
      variant="ghost"
      size="sm"
      disabled
      title="This is a system template managed by administrators"
      data-testid="template-system-indicator"
    >
      <Pencil class="w-3.5 h-3.5 opacity-50" />
    </Button>

    <DropdownMenu v-if="!props.template.is_system_template">
      <DropdownMenuTrigger as-child>
        <Button variant="ghost" class="w-8 h-8 p-0" data-testid="template-dropdown-trigger">
          <span class="sr-only">Open menu</span>
          <MoreHorizontal class="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem @click="remove" class="text-red-600" data-testid="template-delete-action">Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Show disabled dropdown for system templates with explanation -->
    <div v-else class="relative group">
      <Button variant="ghost" class="w-8 h-8 p-0 opacity-50 cursor-not-allowed" disabled>
        <span class="sr-only">System template - cannot be deleted</span>
        <MoreHorizontal class="w-4 h-4" />
      </Button>
      <div class="absolute right-0 top-full mt-1 w-56 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
        This is a system template managed by administrators and cannot be modified or deleted.
      </div>
    </div>
  </div>
</template>
